import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ChoiceBarsPlotData, ChoicePlotView } from '@digital-twins/deck-ui/plots/choice-bars'
import { RankingBarsPlot, quickStudyRankingAdapter } from '@digital-twins/deck-ui/plots/ranking-bars'
import { useRankingBarsLabels } from '@/features/studies/report/page/components/views/analysis/useRankingBarsLabels'

import { Result } from './quickStudyTypes'

/**
 * A quick-study ranking read drawn from Python's `ranking_plot` (plots plan I),
 * with the first-choice view (W7 Q5) when Python stamped it as the plot's
 * `choice-bars` alternative (`ranking_scores.first_choices`, the escape typed).
 * A toggle switches between the two views; both are the stamped figures. The
 * ranking chrome is in the viewer's UI language (owner decision 5). Returns
 * null when the read carries no plot (stored results keep `RankingChart`).
 *
 * Also draws a monadic yes / no read (W7 Q2): each item's yes share is a
 * `ranking-bars` plot on the same field, with no alternative.
 */

export type QuickStudyRankingView = 'ranking' | 'first-choice'

export interface QuickStudyRankingPlotProps {
  result: Pick<Result, 'ranking_plot'>
  /** The view to open on; the first-choice view only when one was stamped. */
  initialView?: QuickStudyRankingView
  /** The card's own switch picks the view: draw `initialView` with no in-plot toggle (one switch per choice). */
  controlled?: boolean
  testIdPrefix?: string
}

/** The stamped first-choice alternative's data, if any. */
export function firstChoiceAlternative(alternatives: unknown): ChoiceBarsPlotData | null {
  if (!Array.isArray(alternatives)) {
    return null
  }
  const found = alternatives.find(
    (entry) => typeof entry === 'object' && entry !== null && (entry as { kind?: unknown }).kind === 'choice-bars',
  ) as { data?: unknown } | undefined
  const data = found?.data
  if (typeof data !== 'object' || data === null || !Array.isArray((data as { series?: unknown }).series)) {
    return null
  }
  return data as ChoiceBarsPlotData
}

/** Whether a result can draw through `QuickStudyRankingPlot` (a stamped plot is present). */
export function hasQuickStudyRankingPlot(result: Pick<Result, 'ranking_plot'>): boolean {
  return quickStudyRankingAdapter.accepts(result)
}

export function QuickStudyRankingPlot(props: QuickStudyRankingPlotProps) {
  const { result, initialView = 'ranking', controlled = false, testIdPrefix = 'qs-ranking-bars' } = props

  const [view, setView] = useState<QuickStudyRankingView>(initialView)

  const { t, i18n } = useTranslation('app')
  const rankingBarsLabels = useRankingBarsLabels()

  if (!quickStudyRankingAdapter.accepts(result)) {
    return null
  }
  const plot = quickStudyRankingAdapter.read(result)
  const firstChoice = firstChoiceAlternative(plot.alternatives)
  const shown: QuickStudyRankingView = firstChoice ? (controlled ? initialView : view) : 'ranking'
  const ranking = <RankingBarsPlot data={{ ...plot.data, labels: rankingBarsLabels }} size="card" testIdPrefix={testIdPrefix} />
  if (!firstChoice) {
    return ranking
  }
  const otherToggleLabels = {
    expand: (count: number) => t('studyReport.plotOther.expand', { count }),
    collapse: t('studyReport.plotOther.collapse'),
  }
  if (controlled) {
    return shown === 'first-choice' ? (
      <ChoicePlotView
        data={firstChoice}
        locale={i18n.language}
        otherToggleLabels={otherToggleLabels}
        testIdPrefix={`${testIdPrefix}-first-choice`}
      />
    ) : (
      ranking
    )
  }
  const words: Record<QuickStudyRankingView, string> = {
    ranking: t('studyReport.plotToggle.ranking'),
    'first-choice': t('studyReport.plotToggle.firstChoice'),
  }
  return (
    <div className="flex w-full flex-col gap-3" data-testid={`${testIdPrefix}-views`}>
      <div
        role="group"
        aria-label={t('studyReport.plotToggle.rankingViews')}
        className="flex justify-end gap-1"
        data-testid={`${testIdPrefix}-view-toggle`}
      >
        {(['ranking', 'first-choice'] as Array<QuickStudyRankingView>).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={shown === option}
            data-testid={`${testIdPrefix}-view-${option}`}
            onClick={() => setView(option)}
            className={
              shown === option
                ? 'rounded-md border border-border bg-muted px-2 py-0.5 text-xs text-foreground'
                : 'rounded-md border border-transparent px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground'
            }
          >
            {words[option]}
          </button>
        ))}
      </div>
      {shown === 'first-choice' ? (
        <ChoicePlotView
          data={firstChoice}
          locale={i18n.language}
          otherToggleLabels={otherToggleLabels}
          testIdPrefix={`${testIdPrefix}-first-choice`}
        />
      ) : (
        ranking
      )}
    </div>
  )
}
